import { useApolloClient } from '@vue/apollo-composable';

/**
 * Composable to access both Apollo clients
 *
 * MIGRATION STRATEGY:
 * - Use Nexus client for ALL NEW functionality
 * - Use Realm client ONLY for existing functionality during migration
 * - Gradually migrate existing features from Realm to Nexus
 *
 * @param clientName - Specify which client to use ('nexus' for new features, 'realm' for existing)
 * @returns Apollo client instance
 */
export function useApolloClients(clientName?: 'realm' | 'nexus') {
  const { resolveClient } = useApolloClient();

  if (clientName) {
    return resolveClient(clientName);
  }

  return {
    realmClient: resolveClient('realm'), // Legacy MongoDB Realm client
    nexusClient: resolveClient('nexus') // New Nexus client (default)
  };
}

/**
 * Get the Realm (MongoDB) Apollo client
 *
 * ⚠️  LEGACY USE ONLY - Do not use for new functionality
 * Only use this for existing features that haven't been migrated yet
 */
export function useRealmClient() {
  const { resolveClient } = useApolloClient();
  return resolveClient('realm');
}

/**
 * Get the Nexus Apollo client
 *
 * ✅ USE THIS FOR ALL NEW FUNCTIONALITY
 * This is the default client and should be used for all new features
 */
export function useNexusClient() {
  const { resolveClient } = useApolloClient();
  return resolveClient('nexus');
}

/**
 * Default export for new functionality - always use Nexus
 *
 * ✅ Recommended for new components and features
 */
export default function useDefaultClient() {
  return useNexusClient();
}
